import React, {BaseSyntheticEvent, ReactNode, Fragment, useEffect} from 'react';
import { get } from 'lodash';
import {ErrorMessage, FieldArray, FormikProps} from 'formik';
import styled from '@emotion/styled';

import { i18n, objectToChunks } from '@selfserve/react-utils';
import {
  StyledButton,
  StyledCheckboxFormControl,
  StyledDivider, StyledErrorLabel,
  StyledFormGroup,
  StyledInput, StyledJsonContainer,
  StyledLabel,
  StyledSelectMenu,
  StyledText,
} from '@selfserve/react-ui';

import { FormSchema, FormSchemaItem } from '../types';

import { fieldsDirection, formTypes, removeButtonPosition } from '../constants/form-config';

import { StyledControlledListContainer } from '../ui/admin-styled-components';
import { AddValueButton } from '../ui/add-value-button';
import { RemoveValueButton } from '../ui/remove-value-button';
import {withErrorHandling} from "@selfserve/react-hocs";

const StyledAddKeyButton = styled(StyledButton)(({ theme }: { theme?: any }) => ({
  width: '104px',
  margin: `auto 0 0 ${theme.spacing(1)}`,
}));

const StyledIcon = styled.img(({ theme }: { theme?: any }) => ({
  height: '90px',
  width: '90px',
}));

const renderRowFormGroup = (hasObject: boolean, children: ReactNode, key?: string | number) => (
  hasObject ? (
    children
  ) : (
    <StyledFormGroup
      smallMargin
      row
      rowSmallMargin
      smallLabel
      key={key}
    >
      {children}
    </StyledFormGroup>
  )
);

const getJsonStrName = (name: string) => (
  ((): string => {
    return name+"_";
  })()
);

const jsonValueSetFirstTimeString = (name: string, value: object, formik: FormikProps<any>) => (
  (() => {
        formik.setFieldValue(name, JSON.stringify(value,null,2));
  })()
);

const jsonOnChange = (name: string, strName: string, formik: FormikProps<any>) => (
  (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    formik.setFieldValue(strName, e.target.value);
    try {
      formik.setFieldValue(name, JSON.parse(e.target.value));
      formik.setFieldError(name, undefined);
    }
    catch (e: any) {
      formik.setFieldError(name, e.message);
    }
  }
);

const parseJsonString = (value: any) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

export const getFormBySchema = (
  schema: FormSchema,
  formik: FormikProps<any>,
  disabled: boolean,
  isRow: boolean = false,
): ReactNode => {
    const { values, errors} = formik;
    return Object.entries(schema).map(([name, config]: [string, FormSchemaItem]) => {
      switch (config.type) {
      default:
      case formTypes.STRING:
        return (
          <Fragment key={name}>
            <StyledFormGroup smallLabel smallMargin bold withoutMargin={isRow}>
              <StyledInput
                name={name}
                value={get(formik.values, name) || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputLabel={config.title}
                size="small"
                disabled={disabled}
              />
            </StyledFormGroup>
            {config.showDivider && <StyledDivider />}
          </Fragment>
        )
      case formTypes.NUMBER:
        return (
          <Fragment key={name}>
            <StyledFormGroup smallLabel smallMargin bold withoutMargin={isRow}>
              <StyledLabel>
                {config.title}{' '}
                <span>{i18n.t('common.use-numbers-only') as string}</span>
              </StyledLabel>
              <StyledInput
                name={name}
                value={get(formik.values, name) || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                size="small"
                type="number"
                disabled={disabled}
              />
            </StyledFormGroup>
            {config.showDivider && <StyledDivider />}
          </Fragment>
        )
      case formTypes.CHECKBOX:
        return (
          <Fragment key={name}>
            <StyledFormGroup isFormControl smallMargin smallLabel bold withoutMargin={isRow}>
              <StyledCheckboxFormControl
                defaultChecked={get(formik.values, name) as boolean}
                value={get(formik.values, name) || false}
                key={String(get(formik.values, name))}
                onClick={() =>
                  formik.handleChange({
                    target: {
                      value: !get(formik.values, name),
                      name,
                    },
                  })
                }
                label={config.title}
                disabled={disabled}
              />
            </StyledFormGroup>
            {config.showDivider && <StyledDivider />}
          </Fragment>
        )
      case formTypes.SELECT:
        return (
          <Fragment key={name}>
            <StyledFormGroup smallLabel smallMargin bold withoutMargin={isRow}>
              <StyledSelectMenu
                fullWidth
                options={config.options}
                value={String(get(formik.values, name)) || ''}
                defaultValue={String(get(formik.values, name))}
                onChange={(value: string) => {
                  formik.handleChange({
                    target: {
                      value: !isNaN(+value) ? +value : value,
                      name,
                    },
                  })
                }}
                inputLabel={config.title}
                size="small"
                disabled={disabled}
              />
            </StyledFormGroup>
            {config.showDivider && <StyledDivider />}
          </Fragment>
        )
      case formTypes.IMAGE:
        return (
          <Fragment key={name}>
            <StyledFormGroup smallLabel smallMargin withoutMargin={isRow}>
              <StyledLabel>{config.title}</StyledLabel>
              <StyledIcon src={get(formik.values, name) as string} />
            </StyledFormGroup>
            {config.showDivider && <StyledDivider />}
          </Fragment>
        )
      case formTypes.ARRAY:
        return (
          <Fragment key={name}>
            <FieldArray name={name}>
              {(arrayHelper) => (
                <>
                  {config.title && (
                    <StyledText
                      withGrey
                      smallMargin
                      withMargin
                      bold={config.subTitle}
                      fontSize={config.subTitle ? 18 : 20}
                    >
                      {config.title}
                    </StyledText>
                  )}
                  {get(formik.values, name)?.map((value: any, index: number) => {
                    const nestedSchema: FormSchema = {};
                    if (config?.item?.properties) {
                      Object.entries(config.item.properties).forEach(([nestedName, config]) => {
                        nestedSchema[[`${name}[${index}]`, nestedName].join('.')] = config;
                      });
                    } else {
                      nestedSchema[[name, `[${index}]`].join('')] = config.item as FormSchemaItem;
                    }
                    return (
                      <StyledControlledListContainer key={index}>
                        {config?.item?.direction === fieldsDirection.ROW ? (
                          objectToChunks(nestedSchema).map((chunkSchema, index) => {
                            const hasObject = !!Object.values(chunkSchema as FormSchema).find(
                              ({ type }) => type === formTypes.ARRAY || type === formTypes.OBJECT
                            );
                            return renderRowFormGroup(
                              hasObject,
                              getFormBySchema(chunkSchema, formik, disabled, !hasObject),
                              index,
                            );
                          })
                        ) : (
                          getFormBySchema(nestedSchema, formik, disabled)
                        )}
                        <RemoveValueButton
                          leftSide={config.removeButtonPosition === removeButtonPosition.LEFT}
                          onClick={() => arrayHelper.remove(index)}
                          disabled={disabled}
                        />
                      </StyledControlledListContainer>
                    )
                  })}
                  <AddValueButton
                    onClick={() => arrayHelper.push(config?.item?.template)}
                    disabled={disabled}
                  />
                </>
              )}
            </FieldArray>
            {config.showDivider && <StyledDivider />}
          </Fragment>
        )
      case formTypes.OBJECT: {
        const nestedSchema: FormSchema = {};
        Object.entries(config.properties as FormSchema).forEach(([nestedName, config]) => {
          nestedSchema[[name, nestedName].join('.')] = config;
        });
        return (
          <Fragment key={name}>
            {config.title && (
              <StyledText
                withGrey
                smallMargin
                withMargin
                bold={config.subTitle}
                fontSize={config.subTitle ? 18 : 20}
              >
                {config.title}
              </StyledText>
            )}
            {config?.direction === fieldsDirection.ROW ? (
              objectToChunks(nestedSchema).map((chunkSchema, index) => {
                const hasObject = !!Object.values(chunkSchema as FormSchema).find(
                  ({ type }) => type === formTypes.ARRAY || type === formTypes.OBJECT
                );
                return renderRowFormGroup(
                  hasObject,
                  getFormBySchema(chunkSchema, formik, disabled, !hasObject),
                  index,
                );
              })
            ) : (
              getFormBySchema(nestedSchema, formik, disabled)
            )}
            {config.showDivider && <StyledDivider />}
          </Fragment>
        )
      }
      case formTypes.DYNAMIC_OBJECT: {
        const dynamicObject = get(formik.values, name);
        const values = Object.entries(dynamicObject || {});
        return (
          <Fragment key={name}>
            {config.title && (
              <StyledText
                withGrey
                smallMargin
                withMargin
                bold={config.subTitle}
                fontSize={config.subTitle ? 18 : 20}
              >
                {config.title}
              </StyledText>
            )}
            {values.map(([key, value], index, arr) =>
              <StyledControlledListContainer key={key}>
                <StyledFormGroup smallLabel bold smallMargin={index !== arr.length - 1}>
                  <StyledInput
                    name={[name, key].join('.')}
                    value={value || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    inputLabel={key}
                    disabled={disabled}
                    size="small"
                  />
                </StyledFormGroup>
                <RemoveValueButton
                  leftSide={config.removeButtonPosition === removeButtonPosition.LEFT}
                  onClick={() => {
                    const newValue = { ...dynamicObject };
                    delete newValue[key];
                    formik.setFieldValue(name, newValue);
                  }}
                  disabled={disabled}
                />
              </StyledControlledListContainer>
            )}
            {renderRowFormGroup(false,
              <>
                <StyledInput
                  placeholder={i18n.t('common.add-property-name')}
                  size="small"
                  disabled={disabled}
                  fullWidth
                />
                <StyledAddKeyButton
                  onClick={(event: BaseSyntheticEvent) => {
                    const input = event.target.parentNode.querySelector('input');
                    const key = input.value || 'newKey';
                    const newValue = { ...dynamicObject };
                    newValue[key] = '';
                    formik.setFieldValue(name, newValue);
                    input.value = '';
                  }}
                  disabled={disabled}
                  withoutMargin
                  isSmall
                >
                  {i18n.t('common.add-key') as string}
                </StyledAddKeyButton>
              </>
            )}
            {config.showDivider && <StyledDivider />}
          </Fragment>
        );
      }
      case formTypes.JSON_STRING: {
        const value = get(formik.values, name)
        const strName = getJsonStrName(name)
        const strValue = get(formik.values, strName)
        if (!strValue && value) {
          try {
            jsonValueSetFirstTimeString(strName, value, formik)
          } catch {}
        }
        return (
          <Fragment key={name}>
            <StyledFormGroup smallLabel smallMargin bold withoutMargin={isRow}>
              <StyledLabel>{config.title}</StyledLabel>
              <StyledJsonContainer
                name={name}
                value={strValue}
                onChange={jsonOnChange(name, strName, formik)}
                onBlur={(e) => formik.handleBlur(e)}
                disabled={disabled}
                error={!!errors[name]}
              />
            </StyledFormGroup>
            <StyledErrorLabel>{!!errors[name] ? errors[name] + "" : ""}</StyledErrorLabel>
            {config.showDivider && <StyledDivider />}
          </Fragment>
        )
      }
    }
  });
};
